import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import hexRgba from 'hex-rgba';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { color } from '../../app/theme';
import { changeSeconds } from '../../app/utils';
import defaultPNG from '../../assets/images/png/img-default-tamplate-img@3x.png';
import imgDoc from '../../assets/images/svg/img-file-doc.svg';
import defaultImageUrl from '../../assets/images/svg/img_avatar_org.svg';
import {
  HashtagUrlText,
  WaTempPlaceholderUrlText,
} from '../shared/HashtagText';
import ImageDisplay from '../shared/ImageDisplay';
import WhatsAppListCard from './previews/WhatsAppListCard';

const useStyles = makeStyles(
  {
    root: {
      paddingBottom: 24,
    },
    line: {
      flexGrow: 1,
      borderBottom: `1px solid ${color.$9}`,
    },
    waitText: {
      margin: '0 16px',
      maxWidth: '80%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      letterSpacing: 'normal',
      color: color.$11,
    },
    order: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 24,
    },
    row: {
      display: 'flex',
      minHeight: 52,
      marginBottom: 12,
    },
    text: {
      borderRadius: 12,
      backgroundColor: color.$8,
      fontSize: 13,
      color: color.$13,
      padding: '8px 12px',
      marginBottom: 16,
      maxWidth: 424,
      width: 'max-content',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      '& .textMessageLink': {
        color: color.$13,
        textDecoration: 'underline',
        '&:hover': {
          opacity: 0.6,
        },
      },
    },
    content: {
      marginBottom: 16,
    },
    audio: {
      borderRadius: 12,
      width: 249,
    },
    video: {
      borderRadius: 12,
      width: 249,
      height: 148,
    },
    image: {
      width: 249,
      borderRadius: 16,
    },
    imageMap: {
      width: 305,
      borderRadius: 16,
    },
    templateBlock: {
      width: 204,
      marginBottom: 12,
      display: 'flex',
      flexDirection: 'column',
    },
    templateContent: porps => ({
      height: '100%',
      borderRadius: 12,
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      backgroundColor: color.$7,
      display: porps.buttonIsFullWidth ? 'flex' : 'block',
      flexDirection: 'column',
    }),
    'templateContent--isImage': {
      position: 'relative',
    },
    templateImage: {
      objectFit: 'cover',
      display: 'block',
      width: 204,
      borderRadius: '12px 12px 0 0',
      margin: 0,
    },
    'templateImage--type-url': {
      width: 204,
      height: 204,
    },
    'templateImage--isImage': {
      borderRadius: 12,
    },
    templateTitle: {
      color: color.$3,
      fontSize: 13,
      padding: '12px 12px 4px 12px',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
    },
    templateSubTitle: porps => ({
      padding: porps.buttonIsFullWidth ? '0 12px 12px 12px' : 16,
      color: color.$11,
      fontSize: 13,
      lineHeight: 'normal',
      flex: porps.buttonIsFullWidth ? 1 : 'none',
      wordWrap: 'break-word',
      whiteSpace: 'pre-wrap',
    }),
    btn: props => ({
      color: color.$2,
      fontSize: 13,
      lineHeight: 1,
      padding: '12px 16px',
      width: props.buttonIsFullWidth ? '100%' : '50%',
      display: props.buttonIsFullWidth ? 'block' : 'inline-block',
      textAlign: 'center',
      verticalAlign: 'top',
      borderTop: `1px solid ${hexRgba(color.$9, 60)}`,
      wordWrap: 'break-word',
      position: 'relative',
    }),
    templateImageCard: {
      display: 'flex',
      overflowX: 'auto',
      paddingBottom: 8,
      backgroundColor: color.$7,
      width: '100%',
      marginBottom: 16,
      '& > div + div': {
        marginLeft: 8,
      },
    },
    card: {
      display: 'flex',
      overflowX: 'auto',
      paddingBottom: 8,
      backgroundColor: color.$7,
      width: '100%',
      marginBottom: 16,
      '& > div + div': {
        marginLeft: 8,
      },
    },
    confirm: {
      marginBottom: 16,
    },
    action: {
      position: 'absolute',
      border: 'solid 1px #2ca6e0',
      backgroundColor: 'rgba(44, 166, 224, 0.4)',
    },
    tempBtnIndex: {
      position: 'absolute',
      minWidth: 20,
      height: 20,
      color: '#fff',
      backgroundColor: '#000',
      textAlign: 'center',
      borderRadius: '11px',
      padding: '4px 8px',
      fontSize: 13,
      fontWeight: 500,
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 1,
      letterSpacing: 'normal',
    },
    confirmAndCardTempBtnIndex: {
      top: 'calc(50% - 10px)',
      left: '4px',
    },
    confirmButtonTitle: {
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: 14,
    },
    imageTempBtnIndex: {
      top: '4px',
      left: '4px',
    },
    imageMapTempBtnIndex: {
      top: '0px',
      left: '0px',
    },
    richvideo: {
      width: 414,
      borderRadius: 12,
    },
    playerIconBlock: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'max-content',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 5,
    },
    playerIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '6px 8px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$7,
      '& > img': {
        marginRight: 9,
      },
    },
    playerIndex: {
      width: '100%',
      height: '48px',
      position: 'absolute',
      left: 0,
      zIndex: 6,
      '& > a': {
        width: '100%',
        height: '100%',
        padding: '4px',
      },
    },
    playerCover: {
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      position: 'absolute',
      borderRadius: '12px',
      zIndex: 4,
    },
    fileTitle: {
      color: color.$7,
      padding: '8px 12px',
      borderRadius: '12px',
      marginBottom: '8px',
      backgroundColor: color.$12,
      marginTop: 12,
    },
    fileImg: {
      borderRadius: 10,
    },
  },
  {
    name: 'PreviewMessage',
  },
);

function Template({
  type,
  imageType,
  imageUrl,
  subTitle,
  title,
  buttons,
  elementTempBtnIndexTotal,
  isHideIndex,
}) {
  const isWaList = type === 'waList';
  const isCard = type === 'card';
  const isImage = type === 'image';
  const classes = useStyles({ buttonIsFullWidth: isCard || isWaList });
  const [bgSrc, setBgSrc] = useState(defaultPNG);
  const tempBtnIndex = elementTempBtnIndexTotal - buttons.length;

  useEffect(() => {
    if (imageUrl) {
      const bgImg = new Image();
      bgImg.onload = () => {
        setBgSrc(bgImg.src);
      };
      bgImg.src = imageUrl;
    }
  }, []);

  const btnContent = () =>
    (buttons || []).map((btn, i) => {
      const { title: btnTitle } = btn;
      return (
        <div key={`${btnTitle}-${uuidv4()}`} className={classes.btn}>
          {!isHideIndex && (
            <div
              className={classnames(
                classes.confirmAndCardTempBtnIndex,
                classes.tempBtnIndex,
              )}
            >
              {tempBtnIndex + i}
            </div>
          )}
          <span className={classes.confirmButtonTitle}>{btnTitle}</span>
        </div>
      );
    });

  return (
    <div className={classes.templateBlock}>
      <div
        className={classnames(classes.templateContent, {
          [classes['templateContent--isImage']]: isImage,
        })}
      >
        {(isCard || isImage || isWaList) && (
          <>
            {isImage && !isHideIndex && (
              <div
                className={classnames(
                  classes.imageTempBtnIndex,
                  classes.tempBtnIndex,
                )}
              >
                {tempBtnIndex}
              </div>
            )}
            {!isWaList && (
              <img
                className={classnames(classes.templateImage, {
                  [classes['templateImage--type-url']]: imageType === 'url',
                  [classes['templateImage--isImage']]: isImage,
                })}
                src={bgSrc}
                alt={title}
              />
            )}
            {(isCard || isWaList) && (
              <div className={classes.templateTitle}>{title}</div>
            )}
          </>
        )}
        {!isImage && (
          <>
            <div className={classes.templateSubTitle}>{subTitle}</div>
            {btnContent()}
          </>
        )}
      </div>
    </div>
  );
}

const PreviewMessage = ({ node, orgIconURL = defaultImageUrl }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data } = node || {};
  const { waitTime, messages } = data || {};

  const waitText = useMemo(() => {
    const year = changeSeconds(waitTime, 'year');
    const month = changeSeconds(waitTime, 'month');
    const day = changeSeconds(waitTime, 'day');
    const hour = changeSeconds(waitTime, 'hour');
    const minute = changeSeconds(waitTime, 'minute');

    if (waitTime === null || (!year && !month && !day && !hour && !minute)) {
      return t('message-node.not-wait');
    }

    return t('message-node.wait', {
      text: `${year ? `${t('message-node.year', { year })}` : ''}${
        month ? `${t('message-node.month', { month })}` : ''
      }${day ? `${t('message-node.day', { day })}` : ''}${
        hour ? `${t('message-node.hour', { hour })}` : ''
      }${minute ? `${t('message-node.minute', { minute })}` : ''}`,
    });
  }, [waitTime]);

  const renderActionBlock = (
    msgData,
    elementTempBtnIndexTotal,
    isHideIndex,
  ) => {
    const screenSize = 305;
    const { size, buttons } = msgData || {};
    const maxHeight = size?.height * (screenSize / size?.width);
    const tempBtnIndex = elementTempBtnIndexTotal - buttons.length;
    return buttons.map((button, index) => {
      const { height, width, x, y, title, type } = button;
      if (type === 'url') {
        return (
          <a
            key={`ImageMapActionBlock-${index}`}
            className={classes.action}
            href={title}
            rel="noopener noreferrer"
            aria-label="message link"
            style={{
              height: parseFloat(height) * 0.01 * maxHeight,
              width: parseFloat(width) * 0.01 * screenSize,
              top: parseFloat(y) * 0.01 * maxHeight,
              left: parseFloat(x) * 0.01 * screenSize,
            }}
            target="_blank"
          >
            {!isHideIndex && (
              <div
                className={classnames(
                  classes.imageMapTempBtnIndex,
                  classes.tempBtnIndex,
                )}
              >
                {tempBtnIndex + index}
              </div>
            )}
            <i className="fa fa-phone" aria-hidden="true" />
          </a>
        );
      }
      return (
        <div
          key={`ImageMapActionBlock-${index}`}
          className={classes.action}
          style={{
            height: parseFloat(height) * 0.01 * maxHeight,
            width: parseFloat(width) * 0.01 * screenSize,
            top: parseFloat(y) * 0.01 * maxHeight,
            left: parseFloat(x) * 0.01 * screenSize,
          }}
        >
          {!isHideIndex && (
            <div
              className={classnames(
                classes.imageMapTempBtnIndex,
                classes.tempBtnIndex,
              )}
            >
              {tempBtnIndex + index}
            </div>
          )}
        </div>
      );
    });
  };

  let elementTempBtnIndex = 1;

  const previewComponents = messages.map(message => {
    switch (
      message?.templateType ||
      message?.data.templateType ||
      message?.contentType
    ) {
      case 'text/plain':
      case 'text':
        const isWhatsappTemplate = !!message?.waTemplateId;

        return (
          <div key={`preview-${message.index}`} className={classes.row}>
            <ImageDisplay
              style={{
                objectFit: 'contain',
                borderRadius: '50%',
                marginRight: 12,
              }}
              src={orgIconURL}
              width={44}
              height={44}
            />
            <div
              className={classes.text}
              dangerouslySetInnerHTML={{
                __html:
                  isWhatsappTemplate &&
                  message?.data?.defaultParameters &&
                  message?.data?.defaultParameters.length
                    ? WaTempPlaceholderUrlText(message?.data?.content)
                    : HashtagUrlText(message?.data?.content),
              }}
            />
          </div>
        );
      case 'application/x-image':
        return (
          <div key={`preview-${message.index}`} className={classes.row}>
            <ImageDisplay
              style={{
                objectFit: 'contain',
                borderRadius: '50%',
                marginRight: 12,
              }}
              src={orgIconURL}
              width={44}
              height={44}
            />
            <div className={classes.content}>
              <img
                className={classes.image}
                src={message?.data?.url}
                alt={`${message?.data?.name}`}
              />
            </div>
          </div>
        );
      case 'application/x-audio':
        return (
          <div key={`preview-${message.index}`} className={classes.row}>
            <ImageDisplay
              style={{
                objectFit: 'contain',
                borderRadius: '50%',
                marginRight: 12,
              }}
              src={orgIconURL}
              width={44}
              height={44}
            />
            <div className={classes.content}>
              <audio
                className={classes.audio}
                src={message?.data?.url}
                controls
              >
                <track kind="captions" />
              </audio>
            </div>
          </div>
        );
      case 'application/x-video':
        return (
          <div key={`preview-${message.index}`} className={classes.row}>
            <ImageDisplay
              style={{
                objectFit: 'contain',
                borderRadius: '50%',
                marginRight: 12,
              }}
              src={orgIconURL}
              width={44}
              height={44}
            />
            <div className={classes.content}>
              <video
                className={classes.video}
                src={message?.data?.url}
                controls
              >
                <track kind="captions" />
              </video>
            </div>
          </div>
        );
      case 'application/x-file':
        return (
          <div key={`preview-${message.index}`} className={classes.row}>
            <ImageDisplay
              style={{
                objectFit: 'contain',
                borderRadius: '50%',
                marginRight: 12,
              }}
              src={orgIconURL}
              width={44}
              height={44}
            />
            <div>
              {message?.data?.name && (
                <div className={classes.fileTitle}>{message?.data?.name}</div>
              )}
              <img
                alt={message.data.name}
                className={classes.fileImg}
                src={imgDoc}
                width={120}
                height={120}
              />
            </div>
          </div>
        );
      case 'card':
        return (
          <div key={`preview-${message.index}`} className={classes.row}>
            <ImageDisplay
              style={{
                objectFit: 'contain',
                borderRadius: '50%',
                marginRight: 12,
              }}
              src={orgIconURL}
              width={44}
              height={44}
            />
            <div className={classes.card}>
              {message?.data?.elements.map((elm, i) => {
                const { buttons, title, subtitle, imageType, imageUrl } = elm;
                elementTempBtnIndex += buttons.length;
                return (
                  <Template
                    key={`template-card-${i}`}
                    type={message?.data?.templateType}
                    imageType={imageType}
                    imageUrl={imageUrl}
                    title={title}
                    subTitle={subtitle}
                    buttons={buttons}
                    index={i}
                    elementTempBtnIndexTotal={elementTempBtnIndex}
                  />
                );
              })}
            </div>
          </div>
        );
      case 'image':
        return (
          <div key={`preview-${message.index}`} className={classes.row}>
            <ImageDisplay
              style={{
                objectFit: 'contain',
                borderRadius: '50%',
                marginRight: 12,
              }}
              src={orgIconURL}
              width={44}
              height={44}
            />
            <div className={classes.templateImageCard}>
              {message?.data?.elements.map((elm, i) => {
                const { imageType, imageUrl, buttons } = elm;
                elementTempBtnIndex += buttons.length;
                return (
                  <Template
                    key={`template-image-${i}`}
                    type={message?.data?.templateType}
                    imageType={imageType}
                    imageUrl={imageUrl}
                    buttons={buttons}
                    index={i}
                    elementTempBtnIndexTotal={elementTempBtnIndex}
                  />
                );
              })}
            </div>
          </div>
        );
      case 'confirm':
        return (
          <div key={`preview-${message.index}`} className={classes.row}>
            <ImageDisplay
              style={{
                objectFit: 'contain',
                borderRadius: '50%',
                marginRight: 12,
              }}
              src={orgIconURL}
              width={44}
              height={44}
            />
            <div className={classes.confirm}>
              {message?.data?.elements.map((elm, i) => {
                const { buttons, title } = elm;
                elementTempBtnIndex += buttons.length;
                return (
                  <Template
                    key={`template-confirm-${i}`}
                    type={message?.data?.templateType}
                    subTitle={title}
                    buttons={buttons}
                    index={i}
                    elementTempBtnIndexTotal={elementTempBtnIndex}
                  />
                );
              })}
            </div>
          </div>
        );
      case 'imagemap':
        return message?.data?.elements.map(elm => {
          const { buttons } = elm;
          elementTempBtnIndex += buttons.length;
          return (
            <div
              key={`template-x-imagemap-${message.index}`}
              className={classes.content}
              style={{
                position: 'relative',
              }}
            >
              <img
                className={classes.imageMap}
                src={elm.imageUrl}
                alt={elm.title}
              />
              {renderActionBlock(elm, elementTempBtnIndex)}
            </div>
          );
        });
      case 'waList':
        return (
          <div key={`preview-${message.index}`} className={classes.row}>
            <ImageDisplay
              style={{
                objectFit: 'contain',
                borderRadius: '50%',
                marginRight: 12,
              }}
              src={orgIconURL}
              width={44}
              height={44}
            />
            <div className={classes.confirm}>
              {message?.data?.elements.map((elm, i) => {
                elementTempBtnIndex += elm?.buttons?.length;
                return (
                  <WhatsAppListCard
                    key={`default-template-preview-wa-list-card-${i}`}
                    data={elm}
                    messageIndex={0}
                    index={i}
                    elementTempBtnIndexTotal={elementTempBtnIndex}
                    isShowList
                    isShowElementTempBtnIndex
                  />
                );
              })}
            </div>
          </div>
        );
      default:
        return null;
    }
  });

  return (
    <div className={classes.root}>
      <div className={classes.order}>
        <span className={classes.line} />
        <span className={classes.waitText}>{waitText}</span>
        <span className={classes.line} />
      </div>
      {previewComponents}
    </div>
  );
};

export default PreviewMessage;
